import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';

/**
 * The game, from the client's side — المرحلة 5.
 *
 * Everything here READS. A wallet, a badge and a redemption are written by the
 * server alone (functions/gamification.js) and the rules refuse every client
 * write, so the only way a child earns anything is by reciting. The callables
 * below ask the server to act; none of them writes a document directly.
 */

const fns = () => getFunctions(app, FUNCTIONS_REGION);

export type Wallet = {
  /** Points left to spend. */
  balance: number;
  /** Points ever earned — what badges are measured against, never spent. */
  earned: number;
  ayahs: number;
  turns: number;
  cleanTurns: number;
  /** Badge id to the moment it was awarded. */
  badges: Record<string, number>;
  loading: boolean;
};

const EMPTY: Wallet = {
  balance: 0, earned: 0, ayahs: 0, turns: 0, cleanTurns: 0, badges: {}, loading: true,
};

const asNumber = (value: unknown): number =>
  (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/**
 * One child's wallet.
 *
 * A child with no wallet yet is not an error and not a spinner forever: they
 * have recited nothing, so every figure is zero and the store can tell them
 * what a reward costs.
 */
export function useWallet(uid: string | null): Wallet {
  const [wallet, setWallet] = useState<Wallet>(EMPTY);

  useEffect(() => {
    if (!uid) { setWallet({ ...EMPTY, loading: false }); return undefined; }
    setWallet(EMPTY);
    return onSnapshot(
      doc(db, 'wallets', uid),
      (snap) => {
        const held = (snap.exists() ? snap.data() : {}) as Record<string, unknown>;
        const badges = held.badges;
        setWallet({
          balance: asNumber(held.balance),
          earned: asNumber(held.earned),
          ayahs: asNumber(held.ayahs),
          turns: asNumber(held.turns),
          cleanTurns: asNumber(held.cleanTurns),
          badges: badges && typeof badges === 'object' ? badges as Record<string, number> : {},
          loading: false,
        });
      },
      () => setWallet({ ...EMPTY, loading: false }),
    );
  }, [uid]);

  return wallet;
}

export type BadgeRule = {
  id: string;
  name: string;
  metric: 'ayahs' | 'turns' | 'cleanTurns' | 'points';
  threshold: number;
  enabled: boolean;
};

/**
 * The rules of the game, as declared — م-5-02.
 *
 * Closed rules are kept, not filtered: a child holding a badge whose rule was
 * retired still needs it to have a name on their shelf. Screens that list what
 * is still winnable filter on `enabled` themselves.
 */
export function useBadgeRules(): { rules: BadgeRule[]; loading: boolean } {
  const [state, setState] = useState<{ rules: BadgeRule[]; loading: boolean }>(
    { rules: [], loading: true },
  );

  useEffect(() => onSnapshot(
    collection(db, 'badgeRules'),
    (snap) => setState({
      rules: snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          name: typeof data.name === 'string' ? data.name : d.id,
          metric: (data.metric as BadgeRule['metric']) ?? 'ayahs',
          threshold: asNumber(data.threshold),
          enabled: data.enabled !== false,
        };
      }).sort((a, b) => a.threshold - b.threshold),
      loading: false,
    }),
    () => setState({ rules: [], loading: false }),
  ), []);

  return state;
}

export type Reward = { id: string; name: string; cost: number; enabled: boolean };

/** The shelf. Cheapest first, so what a child can afford is at the top. */
export function useRewards(): { rewards: Reward[]; loading: boolean } {
  const [state, setState] = useState<{ rewards: Reward[]; loading: boolean }>(
    { rewards: [], loading: true },
  );

  useEffect(() => onSnapshot(
    collection(db, 'rewards'),
    (snap) => setState({
      rewards: snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          name: typeof data.name === 'string' ? data.name : d.id,
          cost: asNumber(data.cost),
          enabled: data.enabled !== false,
        };
      }).sort((a, b) => a.cost - b.cost),
      loading: false,
    }),
    () => setState({ rewards: [], loading: false }),
  ), []);

  return state;
}

/** What this child has already claimed, so the store can say so — م-5-05. */
export function useMyRedemptions(uid: string | null): { taken: string[]; loading: boolean } {
  const [state, setState] = useState<{ taken: string[]; loading: boolean }>(
    { taken: [], loading: true },
  );

  useEffect(() => {
    if (!uid) { setState({ taken: [], loading: false }); return undefined; }
    return onSnapshot(
      query(collection(db, 'redemptions'), where('studentId', '==', uid), limit(50)),
      (snap) => setState({
        taken: snap.docs
          .map((d) => (d.data() as { rewardId?: unknown }).rewardId)
          .filter((id): id is string => typeof id === 'string'),
        loading: false,
      }),
      () => setState({ taken: [], loading: false }),
    );
  }, [uid]);

  return state;
}

export type BoardStanding = { studentId: string; displayName: string; ayahs: number };

/**
 * A class board's rows — م-5-03.
 *
 * `enabled` is the class's own opt-in flag. Passing false subscribes to
 * nothing: the rules would refuse a pupil the rows anyway, and asking for
 * them would light up an error rather than an empty board.
 */
export function useClassBoard(
  classId: string | null,
  enabled: boolean,
): { standings: BoardStanding[]; loading: boolean; denied: boolean } {
  const [state, setState] = useState<{
    standings: BoardStanding[]; loading: boolean; denied: boolean;
  }>({ standings: [], loading: true, denied: false });

  useEffect(() => {
    if (!classId || !enabled) {
      setState({ standings: [], loading: false, denied: false });
      return undefined;
    }
    setState({ standings: [], loading: true, denied: false });
    return onSnapshot(
      query(collection(db, 'classes', classId, 'standings'), limit(60)),
      (snap) => setState({
        standings: snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            studentId: typeof data.studentId === 'string' ? data.studentId : d.id,
            displayName: typeof data.displayName === 'string' ? data.displayName : '',
            ayahs: asNumber(data.ayahs),
          };
        }),
        loading: false,
        denied: false,
      }),
      // A board the rules refuse is not a broken screen: it is a class whose
      // school has not opened one, and the page says so.
      () => setState({ standings: [], loading: false, denied: true }),
    );
  }, [classId, enabled]);

  return state;
}

export async function redeemReward(rewardId: string): Promise<{ redeemed: string }> {
  const call = httpsCallable<{ rewardId: string }, { redeemed: string }>(fns(), 'redeemReward');
  return (await call({ rewardId })).data;
}

export async function saveBadgeRules(
  rules: Omit<BadgeRule, 'enabled'>[],
): Promise<{ saved: number }> {
  const call = httpsCallable<{ rules: unknown[] }, { saved: number }>(fns(), 'saveBadgeRules');
  return (await call({ rules })).data;
}

export async function saveRewards(
  rewards: Omit<Reward, 'enabled'>[],
): Promise<{ saved: number }> {
  const call = httpsCallable<{ rewards: unknown[] }, { saved: number }>(fns(), 'saveRewards');
  return (await call({ rewards })).data;
}

export async function setClassBoard(
  classId: string,
  on: boolean,
): Promise<{ classId: string; leaderboard: boolean }> {
  const call = httpsCallable<
    { classId: string; on: boolean },
    { classId: string; leaderboard: boolean }
  >(fns(), 'setClassBoard');
  return (await call({ classId, on })).data;
}
